<template>
  <div class="auth-container">
    <h1>Authentication</h1>
    <p>Please choose an option below to proceed with Casdoor.</p>
    <div class="button-group">
      <button @click="redirectToCasdoorSignin" class="auth-button signin-button">
        Login with Casdoor
      </button>
      <button @click="redirectToCasdoorSignup" class="auth-button signup-button">
        Sign up with Casdoor
      </button>
    </div>
    <p class="note">
      You will be redirected to the Casdoor authentication page.
    </p>
  </div>
</template>

<script setup>
import { useCasdoor } from 'casdoor-vue-sdk';

/**
 * @file Auth.vue
 * @description This component provides users with options to log in or sign up using Casdoor.
 * It utilizes the `casdoor-vue-sdk` to get the appropriate redirection URLs for Casdoor's
 * authentication pages.
 */

/**
 * Casdoor SDK composable.
 * Provides methods to interact with Casdoor, such as getting sign-in/sign-up URLs.
 * @type {import('casdoor-vue-sdk').UseCasdoor}
 */
const { getSigninUrl, getSignupUrl } = useCasdoor();

/**
 * Redirects the user to the Casdoor sign-in page.
 * This function retrieves the sign-in URL from the Casdoor SDK and performs a client-side redirect.
 */
const redirectToCasdoorSignin = () => {
  const signinUrl = getSigninUrl();
  if (signinUrl) {
    window.location.href = signinUrl;
  } else {
    console.error("Failed to get Casdoor signin URL. Check SDK configuration.");
    // Optionally, display an error message to the user here.
  }
};

/**
 * Redirects the user to the Casdoor sign-up page.
 * This function retrieves the sign-up URL from the Casdoor SDK and performs a client-side redirect.
 * The sign-up URL may differ from the sign-in URL and can lead directly to a registration form.
 */
const redirectToCasdoorSignup = () => {
  const signupUrl = getSignupUrl();
  if (signupUrl) {
    window.location.href = signupUrl;
  } else {
    console.error("Failed to get Casdoor signup URL. Check SDK configuration.");
    // Optionally, display an error message to the user here.
  }
};
</script>

<style scoped>
.auth-container {
  max-width: 450px;
  margin: 50px auto;
  padding: 30px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  text-align: center;
  font-family: Arial, sans-serif;
}

h1 {
  color: #333;
  margin-bottom: 15px;
}

p {
  color: #555;
  margin-bottom: 25px;
  line-height: 1.6;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 25px;
}

.auth-button {
  padding: 12px 20px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
  color: white;
}

.auth-button:hover {
  transform: translateY(-2px);
}

.signin-button {
  background-color: #007bff;
}

.signin-button:hover {
  background-color: #0056b3;
}

.signup-button {
  background-color: #28a745;
}

.signup-button:hover {
  background-color: #1e7e34;
}

.note {
  font-size: 0.9em;
  color: #777;
}
</style>
